import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, DropdownMenu, DropdownMenuItem, Spinner, Toast } from '../components/common';
import type { ToastVariant } from '../components/common';
import WordingSheet from '../components/translation/WordingSheet';
import ConflictsPanel from '../components/translation/ConflictsPanel';
import { Reader, ReaderChapter, ReaderHandle, ReaderOverlay } from '../components/reader/Reader';
import {
  charToWordOffset,
  countWords,
  ReaderSelection,
  splitSentences,
  SourceSentence,
} from '../lib/reader/selection';
import { exportProjectEpub } from '../lib/translation/api';
import type { TranslationSegment } from '../lib/translation/api';
import { useAudiobook } from '../hooks/useAudiobook';
import {
  useAutoTranslateChapter,
  useSaveReadingPosition,
  useSetAutoTranslate,
  useSyncToGithub,
  useTranslateChapter,
  useTranslationProject,
  useUpdateSegment,
} from '../hooks/useTranslationProjects';

// A chapter has every segment translated (and at least one segment).
const chapterDone = (ch?: { segmentCount: number; translatedCount: number }) =>
  !!ch && ch.segmentCount > 0 && ch.translatedCount >= ch.segmentCount;

// The text a segment renders as (translated when available, else the original).
const segDisplayText = (s: TranslationSegment) => s.targetText || s.sourceText || '';

// Sentence highlight classes, applied imperatively (so per-tick progress doesn't
// rebuild paragraph nodes). Kept as literals so Tailwind keeps them in the build.
const PARA_PLAYING = ['rounded', 'bg-amber-50', 'dark:bg-amber-900/20'];
const SENT_PLAYING = ['rounded', 'bg-amber-200', 'dark:bg-amber-700/50'];

// Sentence splitting is reused for rendering and for mapping playback progress to
// a sentence; memoize per text since paragraphs are split repeatedly.
const splitCache = new Map<string, SourceSentence[]>();
const sentencesOf = (text: string): SourceSentence[] => {
  let cached = splitCache.get(text);
  if (!cached) {
    cached = splitSentences(text);
    splitCache.set(text, cached);
  }
  return cached;
};

// Contiguous pieces covering the whole paragraph, each tagged with its sentence
// index (null for inter-sentence gaps), so we can wrap sentences in spans without
// altering the text content the selection/anchor logic relies on.
const sentenceParts = (text: string): { raw: string; index: number | null }[] => {
  const sents = sentencesOf(text);
  const parts: { raw: string; index: number | null }[] = [];
  let cursor = 0;
  sents.forEach((s, i) => {
    if (s.start > cursor) parts.push({ raw: text.slice(cursor, s.start), index: null });
    parts.push({ raw: text.slice(s.start, s.end), index: i });
    cursor = s.end;
  });
  if (cursor < text.length) parts.push({ raw: text.slice(cursor), index: null });
  return parts.length ? parts : [{ raw: text, index: null }];
};

// The sentence index playing at `fraction` (0..1), assuming time is proportional
// to word count — approximate, but enough to follow along.
const sentenceAtFraction = (text: string, fraction: number): number | null => {
  const sents = sentencesOf(text);
  if (sents.length === 0) return null;
  const total = countWords(text) || 1;
  const word = Math.min(total - 1, Math.floor(fraction * total));
  let idx = 0;
  for (let i = 0; i < sents.length; i += 1) {
    if (charToWordOffset(text, sents[i].start) <= word) idx = i;
    else break;
  }
  return idx;
};

export default function TranslationReaderPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // The chapter we last asked autotranslate to generate; drives polling.
  const [pendingTranslateId, setPendingTranslateId] = useState<string | null>(null);
  const { data: project, isLoading } = useTranslationProject(projectId, {
    poll: !!pendingTranslateId,
  });

  const [chapterIndex, setChapterIndex] = useState(0);
  const [selection, setSelection] = useState<ReaderSelection | null>(null);
  const [showConflicts, setShowConflicts] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);

  const readerRef = useRef<ReaderHandle>(null);
  const chapterIndexRef = useRef(chapterIndex);
  chapterIndexRef.current = chapterIndex;

  const updateSegment = useUpdateSegment(projectId);
  const translateChapter = useTranslateChapter(projectId);
  const setAutoTranslateMut = useSetAutoTranslate(projectId);
  const autoTranslateMut = useAutoTranslateChapter(projectId);
  const sync = useSyncToGithub(projectId);
  const savePosition = useSaveReadingPosition();
  const [toast, setToast] = useState<{ message: string; variant: ToastVariant } | null>(null);

  const handleSync = useCallback(() => {
    sync.mutate(undefined, {
      onSuccess: () =>
        setToast({ message: t('translationReader.syncStarted'), variant: 'success' }),
      onError: (err) =>
        setToast({
          message: err instanceof Error ? err.message : t('translationReader.syncFailed'),
          variant: 'error',
        }),
    });
  }, [sync, t]);

  const chapters = useMemo(() => project?.chapters ?? [], [project]);
  const currentChapter = chapters[chapterIndex];
  const segments = useMemo(() => currentChapter?.segments ?? [], [currentChapter]);
  const segmentsById = useMemo(() => {
    const map = new Map<string, TranslationSegment>();
    segments.forEach((s) => map.set(s.externalId, s));
    return map;
  }, [segments]);
  // --- audiobook (narration) ---
  const goToReaderChapter = useCallback((idx: number) => {
    if (idx !== chapterIndexRef.current) readerRef.current?.goToChapter(idx, 'first');
  }, []);
  const scrollToSegment = useCallback((externalId: string) => {
    const anchor = { segmentId: externalId, charOffset: 0 };
    if (!readerRef.current?.scrollToAnchor(anchor))
      requestAnimationFrame(() =>
        requestAnimationFrame(() => readerRef.current?.scrollToAnchor(anchor))
      );
  }, []);
  // Start narrating from the translated line at the top of the page (else the
  // chapter's first translated line).
  const getStartKey = useCallback(() => {
    const segs = currentChapter?.segments ?? [];
    const topId = readerRef.current?.getAnchor()?.segmentId;
    const from = topId ? segs.findIndex((s) => s.externalId === topId) : -1;
    const at = (from >= 0 ? segs.slice(from) : segs).find((s) => s.targetText);
    return at?.externalId ?? null;
  }, [currentChapter]);

  const audiobook = useAudiobook({
    projectId,
    chapters,
    goToChapter: goToReaderChapter,
    scrollToSegment,
    getStartKey,
  });

  // Translated text keyed by segment, for sentence mapping and word-offset seeks.
  const segTextByKey = useMemo(() => {
    const map = new Map<string, string>();
    chapters.forEach((ch) =>
      ch.segments.forEach((s) => {
        if (s.targetText) map.set(s.externalId, s.targetText);
      })
    );
    return map;
  }, [chapters]);

  // Build the normalized chapter model for the shared Reader. Paragraphs are
  // rendered as sentence spans; the playing line + sentence are highlighted
  // imperatively (see below) so per-tick progress never rebuilds these nodes.
  const readerChapters: ReaderChapter[] = useMemo(
    () =>
      chapters.map((ch) => ({
        id: ch.id,
        title: ch.displayTitle,
        ready: chapterDone(ch),
        pending: ch.translating || pendingTranslateId === ch.id,
        blocks: ch.segments.map((segment) => ({
          id: segment.externalId,
          text: segDisplayText(segment),
          node: segment.targetText ? (
            <p
              data-reader-seg={segment.externalId}
              className={[
                'leading-relaxed text-slate-900 dark:text-slate-100',
                segment.conflicted ? 'rounded bg-amber-50 px-1' : '',
              ].join(' ')}
            >
              {sentenceParts(segment.targetText).map((part, i) =>
                part.index === null ? (
                  <span key={i}>{part.raw}</span>
                ) : (
                  <span key={i} data-sent={`${segment.externalId}:${part.index}`}>
                    {part.raw}
                  </span>
                )
              )}
            </p>
          ) : (
            <p
              data-reader-seg={segment.externalId}
              className="italic leading-relaxed text-slate-400"
            >
              {segment.sourceText}
            </p>
          ),
        })),
      })),
    [chapters, pendingTranslateId]
  );

  // Highlight the playing paragraph and (approximately) the sentence being
  // spoken, driven imperatively by playback progress so the 4×/sec updates touch
  // only two spans instead of rebuilding every paragraph node.
  const { playingKey, status: audioStatus, playingFraction } = audiobook;
  const litParaRef = useRef<Element | null>(null);
  const litSentRef = useRef<Element | null>(null);
  // The sentence we last paged to, so we turn the page only when it changes.
  const followedSentRef = useRef<string | null>(null);
  useEffect(() => {
    litParaRef.current?.classList.remove(...PARA_PLAYING, 'animate-pulse');
    litParaRef.current = null;
    litSentRef.current?.classList.remove(...SENT_PLAYING);
    litSentRef.current = null;
    if (!playingKey) {
      followedSentRef.current = null;
      return;
    }

    const para = document.querySelector(`[data-reader-seg="${CSS.escape(playingKey)}"]`);
    if (para) {
      para.classList.add(...PARA_PLAYING);
      if (audioStatus === 'buffering') para.classList.add('animate-pulse');
      litParaRef.current = para;
    }
    const text = segTextByKey.get(playingKey);
    const sents = text != null ? sentencesOf(text) : [];
    const idx = text != null ? sentenceAtFraction(text, playingFraction) : null;
    if (idx == null) return;

    const span = document.querySelector(`[data-sent="${CSS.escape(playingKey)}:${idx}"]`);
    if (!span) return;
    span.classList.add(...SENT_PLAYING);
    litSentRef.current = span;

    // Follow along: when the spoken sentence advances to one that isn't fully on
    // the page, turn to the page that holds it. Only on a sentence change, so we
    // don't fight the reader's paging every progress tick.
    const sentKey = `${playingKey}:${idx}`;
    if (sentKey !== followedSentRef.current) {
      followedSentRef.current = sentKey;
      const surface = span.closest('[data-reader-surface]');
      const sr = surface?.getBoundingClientRect();
      const br = span.getBoundingClientRect();
      const fullyVisible = !sr || (br.top >= sr.top - 1 && br.bottom <= sr.bottom + 1);
      if (!fullyVisible)
        readerRef.current?.scrollToAnchor({ segmentId: playingKey, charOffset: sents[idx].start });
    }
  }, [playingKey, audioStatus, playingFraction, segTextByKey, readerChapters]);

  // --- autotranslate: keep current + next chapter translated, in order ---
  useEffect(() => {
    if (!project?.autoTranslate || !currentChapter) return;
    let targetIdx = -1;
    if (!chapterDone(currentChapter)) targetIdx = chapterIndex;
    else if (chapters[chapterIndex + 1] && !chapterDone(chapters[chapterIndex + 1]))
      targetIdx = chapterIndex + 1;
    if (targetIdx < 0) return;
    const target = chapters[targetIdx];
    const eligible = targetIdx === 0 || chapterDone(chapters[targetIdx - 1]);
    if (!eligible || target.translating || pendingTranslateId === target.id) return;
    setPendingTranslateId(target.id);
    autoTranslateMut.mutate(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.autoTranslate, chapterIndex, chapters, pendingTranslateId]);

  useEffect(() => {
    if (!pendingTranslateId) return;
    const tch = chapters.find((c) => c.id === pendingTranslateId);
    if (!tch || tch.translating || chapterDone(tch)) setPendingTranslateId(null);
  }, [chapters, pendingTranslateId]);

  if (isLoading)
    return (
      <div className="grid min-h-[50vh] place-items-center">
        <Spinner />
      </div>
    );
  if (!project) return <div className="p-8 text-gray-500">{t('common.error')}</div>;

  const selectedSegment = selection ? segmentsById.get(selection.segmentId) : undefined;
  const closeSheet = () => {
    readerRef.current?.clearSelection();
    setSelection(null);
  };
  const autoTranslate = project.autoTranslate;
  const bookUntranslated = project.segmentCount - project.translatedCount;
  const chapterUntranslated = currentChapter
    ? currentChapter.segmentCount - currentChapter.translatedCount
    : 0;

  const shareSelection = (sel: { text: string }) => {
    if (typeof navigator.share === 'function') {
      navigator.share({ title: `“${project.title}”`, text: sel.text }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(sel.text).catch(() => {});
    }
  };

  // Audio-mode footer transport: prev/next line, play-pause, and an exit back
  // to page reading. Replaces the page-turn footer while narration is on.
  const transportBar = audiobook.active ? (
    <div className="flex w-full items-center justify-between gap-2">
      <Button size="sm" variant="secondary" className="shrink-0" onClick={audiobook.exit}>
        📖 {t('translationReader.readAsBook')}
      </Button>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={audiobook.prev}
          title={t('translationReader.prevSegment')}
          className="rounded-lg px-2 py-1.5 text-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-700"
        >
          ⏮
        </button>
        <button
          type="button"
          onClick={audiobook.toggle}
          title={
            audiobook.status === 'playing'
              ? t('translationReader.pause')
              : t('translationReader.play')
          }
          className="grid h-10 w-10 place-items-center rounded-full bg-slate-900 text-lg text-white hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
        >
          {audiobook.status === 'buffering' ? (
            <Spinner size="sm" />
          ) : audiobook.status === 'playing' ? (
            '⏸'
          ) : (
            '▶'
          )}
        </button>
        <button
          type="button"
          onClick={audiobook.next}
          title={t('translationReader.nextSegment')}
          className="rounded-lg px-2 py-1.5 text-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-700"
        >
          ⏭
        </button>
      </div>
      <span className="w-24 shrink-0 truncate text-right text-xs text-slate-400">
        {audiobook.status === 'buffering'
          ? t('translationReader.generatingAudio')
          : audiobook.status === 'error'
            ? t('translationReader.audioFailed')
            : ''}
      </span>
    </div>
  ) : undefined;

  // Autotranslate reading-surface state → the standardized Reader overlay.
  const currentDone = chapterDone(currentChapter);
  const currentTranslating =
    !!currentChapter?.translating || pendingTranslateId === currentChapter?.id;
  const currentEligible = chapterIndex === 0 || chapterDone(chapters[chapterIndex - 1]);
  let overlay: ReaderOverlay | null = null;
  if (currentChapter && !currentDone && chapterUntranslated > 0) {
    if (autoTranslate && currentTranslating) {
      overlay = { kind: 'spinner', message: t('translationReader.translatingChapter') };
    } else if (autoTranslate && !currentEligible) {
      overlay = { kind: 'hint', message: t('translationReader.waitingEarlierChapters') };
    } else if (!autoTranslate) {
      overlay = {
        kind: 'offer',
        message: t('translationReader.chapterUntranslated', "This chapter isn't translated yet."),
        primaryLabel: t('translationReader.translateChapter'),
        onPrimary: () => translateChapter.mutate(currentChapter.id),
        toggleLabel: t('translationReader.enableAutotranslate', 'Enable autotranslate'),
        onToggle: () => setAutoTranslateMut.mutate(true),
      };
    }
  }

  const headerActions = (
    <>
      {project.conflictCount > 0 && (
        <button
          type="button"
          onClick={() => setShowConflicts(true)}
          title={t('translationReader.resolveConflicts')}
          className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-200"
        >
          ⚠ {t('translationReader.conflicts', { count: project.conflictCount })}
        </button>
      )}
      {chapters.length > 0 && (
        <div className="relative shrink-0">
          <Button
            size="sm"
            variant={autoTranslate ? 'primary' : 'secondary'}
            onClick={() => setTranslateOpen((v) => !v)}
            disabled={translateChapter.isPending}
          >
            {translateChapter.isPending
              ? t('translationReader.translating')
              : `${t('translationReader.translate')} ▾`}
          </Button>
          <DropdownMenu
            isOpen={translateOpen}
            onClose={() => setTranslateOpen(false)}
            position="right"
            width="lg"
          >
            <DropdownMenuItem
              onClick={() => {
                setAutoTranslateMut.mutate(!autoTranslate);
                setTranslateOpen(false);
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{t('translationReader.autotranslate')}</span>
                <span
                  className={
                    autoTranslate
                      ? 'text-xs font-semibold text-green-600'
                      : 'text-xs text-slate-400'
                  }
                >
                  {autoTranslate
                    ? t('translationReader.autotranslateOn')
                    : t('translationReader.autotranslateOff')}
                </span>
              </div>
              <div className="text-xs text-slate-400">
                {t('translationReader.autotranslateHint')}
              </div>
            </DropdownMenuItem>
            <div className="my-1 border-t border-slate-100 dark:border-gray-700" />
            <DropdownMenuItem
              disabled={!currentChapter || chapterUntranslated === 0}
              onClick={() => {
                if (currentChapter) translateChapter.mutate(currentChapter.id);
                setTranslateOpen(false);
              }}
            >
              <div className="font-medium">{t('translationReader.translateChapter')}</div>
              <div className="text-xs text-slate-400">
                {chapterUntranslated > 0
                  ? t('translationReader.untranslatedCount', { count: chapterUntranslated })
                  : t('translationReader.allTranslated')}
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={bookUntranslated === 0}
              onClick={() => {
                chapters.forEach((c) => translateChapter.mutate(c.id));
                setTranslateOpen(false);
              }}
            >
              <div className="font-medium">{t('translationReader.translateBook')}</div>
              <div className="text-xs text-slate-400">
                {bookUntranslated > 0
                  ? t('translationReader.untranslatedCount', { count: bookUntranslated })
                  : t('translationReader.allTranslated')}
              </div>
            </DropdownMenuItem>
            <div className="border-t border-slate-100 px-4 py-2 text-[11px] leading-snug text-slate-400 dark:border-gray-700">
              {t('translationReader.translateSafeNote')}
            </div>
          </DropdownMenu>
        </div>
      )}
      <Button
        size="sm"
        variant={audiobook.active ? 'primary' : 'secondary'}
        className="shrink-0"
        onClick={() => (audiobook.active ? audiobook.exit() : audiobook.enter())}
      >
        {audiobook.active
          ? `⏹ ${t('translationReader.stop')}`
          : `▶ ${t('translationReader.listen')}`}
      </Button>
    </>
  );

  const moreMenuItems = (
    <>
      <DropdownMenuItem onClick={() => navigate(`/translate/${project.id}/settings`)}>
        {t('translationReader.settings')}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => exportProjectEpub(project.id, project.slug)}>
        {t('translationReader.exportEpub')}
      </DropdownMenuItem>
      <DropdownMenuItem disabled={sync.isPending} onClick={handleSync}>
        {sync.isPending
          ? t('translationReader.syncing')
          : project.githubSyncConfigured
            ? t('translationReader.sync')
            : t('translationReader.push')}
      </DropdownMenuItem>
    </>
  );

  const breadcrumb = (
    <nav
      aria-label="Breadcrumb"
      className="mb-3 flex min-w-0 flex-wrap items-center gap-x-1.5 text-sm text-gray-500 dark:text-gray-400"
    >
      <button
        type="button"
        onClick={() => navigate('/books')}
        className="hover:text-gray-700 hover:underline dark:hover:text-gray-200"
      >
        {t('books.title')}
      </button>
      <span className="text-gray-400 dark:text-gray-600" aria-hidden="true">
        ›
      </span>
      <button
        type="button"
        onClick={() => navigate('/books?tab=translations')}
        className="hover:text-gray-700 hover:underline dark:hover:text-gray-200"
      >
        {t('books.tabTranslations')}
      </button>
      <span className="text-gray-400 dark:text-gray-600" aria-hidden="true">
        ›
      </span>
      <span className="min-w-0 truncate text-gray-700 dark:text-gray-300" aria-current="page">
        {project.title}
      </span>
    </nav>
  );

  return (
    <>
      <Reader
        ref={readerRef}
        chapters={readerChapters}
        bookTitle={project.title}
        shareTitle={project.title}
        breadcrumb={breadcrumb}
        onExit={() => navigate('/books?tab=translations')}
        initialPosition={
          project.readingPosition
            ? {
                blockId: project.readingPosition.segmentExternalId,
                charOffset: project.readingPosition.charOffset,
              }
            : null
        }
        onPersistPosition={(p) => {
          if (projectId)
            savePosition.mutate({
              projectId,
              segmentExternalId: p.blockId,
              charOffset: p.charOffset,
            });
        }}
        onChapterChange={setChapterIndex}
        onSelect={setSelection}
        onTap={(tap) => {
          // In audio mode, tapping a line starts narration from the tapped word —
          // its proportional position by word count within the paragraph.
          if (audiobook.active && tap.blockId && audiobook.isPlayable(tap.blockId)) {
            const text = segTextByKey.get(tap.blockId) ?? '';
            const total = countWords(text) || 1;
            const word = tap.charOffset != null ? charToWordOffset(text, tap.charOffset) : 0;
            audiobook.seekTo(tap.blockId, word / total);
            return true;
          }
          return false;
        }}
        chromeBusy={translateOpen}
        chromePinned={audiobook.active}
        headerActions={headerActions}
        footerOverride={transportBar}
        moreMenuItems={moreMenuItems}
        overlay={overlay}
      />

      {selection && selectedSegment && (
        <WordingSheet
          selection={selection}
          segmentId={selectedSegment.id}
          sourceText={selectedSegment.sourceText}
          sourceLanguage={project.sourceLanguage}
          targetLanguage={project.targetLanguage}
          saving={updateSegment.isPending}
          onApply={(newText) => {
            updateSegment.mutate({ segmentId: selectedSegment.id, targetText: newText });
            closeSheet();
          }}
          onShare={() => shareSelection(selection)}
          onClose={closeSheet}
        />
      )}

      {showConflicts && (
        <ConflictsPanel
          projectId={project.id}
          chapters={chapters}
          onClose={() => setShowConflicts(false)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          variant={toast.variant}
          duration={toast.variant === 'error' ? 8000 : 4000}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
